/**
 * PickingIssueDialog Component
 *
 * Two-phase dialog for pickers to report issues at the picking station:
 * - Phase 0: Select issue type (Defective Garment or Item Unavailable)
 * - Phase 1: Details form (searchable SKU combobox, quantity stepper, notes)
 *
 * Defective: stock decremented, picklist unchanged (picker grabs another unit)
 * Unavailable: items removed from picklist (existing INVENTORY_ERROR flow)
 *
 * Design: Large touch targets (h-12+) for gloved warehouse hands.
 * Hotkeys: 1 = Defective, 2 = Unavailable (Escape closes dialog)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContentInline,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { AlertTriangle, PackageX, Minus, Plus, ArrowLeft, ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

type IssueType = 'defective' | 'unavailable';

/**
 * One PILE the picker can flag: a picklist group's units for one order SKU.
 * Own stock and the 3PL's back-up stock of the same order SKU are separate
 * piles, so a defective shirt decrements the row it really came from.
 */
export interface SkuOption {
  /** Unique pile key (picklist group + order SKU). */
  key: string;
  /** The ORDER's SKU (issue matching keys on it). */
  sku: string;
  /** The code the picker pulls (the back-up's code for a back-up pile). */
  pickSku: string;
  productName: string;
  availableCount: number;
  /** Stock type of the pile. */
  stockType: 'own' | 'shared' | 'labelled';
  /** Client name for a labelled pile, when known. */
  clientName?: string | null;
  /** The pile's garment unit ids (sent with a defective report). */
  garmentIds: string[];
}

interface PickingIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchNumber: string;
  skuOptions: SkuOption[];
  onSubmit: (
    issueType: IssueType,
    sku: string,
    quantity: number,
    notes?: string,
    garmentUnitIds?: string[],
  ) => void;
  isSubmitting: boolean;
}

export function PickingIssueDialog({
  open,
  onOpenChange,
  batchNumber,
  skuOptions,
  onSubmit,
  isSubmitting,
}: PickingIssueDialogProps) {
  const { t } = useTranslation('production');
  const [phase, setPhase] = useState<0 | 1>(0);
  const [issueType, setIssueType] = useState<IssueType | null>(null);
  const [selectedSku, setSelectedSku] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [notes, setNotes] = useState<string>('');
  const [skuPopoverOpen, setSkuPopoverOpen] = useState(false);

  const wasOpenRef = useRef(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setPhase(0);
      setIssueType(null);
      setSelectedSku('');
      setQuantity(1);
      setNotes('');
    }
    wasOpenRef.current = open;
  }, [open]);

  const selectedOption = skuOptions.find((s) => s.key === selectedSku);
  const maxQuantity = selectedOption?.availableCount || 1;

  const pileStockLabel = (opt: SkuOption): string => {
    if (opt.stockType === 'labelled') {
      return opt.clientName
        ? t('picking.badges.clientNameLabelled', { client: opt.clientName })
        : t('picking.badges.clientLabelled');
    }
    return opt.stockType === 'shared' ? t('picker.issue.pileShared') : t('picker.issue.pileOwn');
  };

  const selectIssueType = useCallback((type: IssueType) => {
    setIssueType(type);
    setPhase(1);
  }, []);

  const goBack = useCallback(() => {
    setPhase(0);
    setIssueType(null);
    setSelectedSku('');
    setQuantity(1);
    setNotes('');
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture when typing in inputs
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (phase === 0) {
        if (e.key === '1') {
          e.preventDefault();
          selectIssueType('defective');
        } else if (e.key === '2') {
          e.preventDefault();
          selectIssueType('unavailable');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, phase, selectIssueType, goBack]);

  const handleSubmit = () => {
    if (issueType && selectedOption && quantity > 0) {
      onSubmit(
        issueType,
        selectedOption.sku,
        quantity,
        notes || undefined,
        // Defective only: the flagged pile decides which row is decremented.
        issueType === 'defective' ? selectedOption.garmentIds.slice(0, 1000) : undefined,
      );
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 1;
    setQuantity(Math.max(1, Math.min(value, maxQuantity)));
  };

  const adjustQuantity = (delta: number) => {
    setQuantity((prev) => Math.max(1, Math.min(prev + delta, maxQuantity)));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        {phase === 0 ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                {t('picker.issue.title')}
              </DialogTitle>
              <DialogDescription>
                {t('picker.issue.whatKind', { batchNumber })}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3 py-4">
              <button
                type="button"
                onClick={() => selectIssueType('defective')}
                className="flex items-start gap-4 rounded-lg border-2 border-red-200 bg-red-50 p-4 text-left transition-colors hover:border-red-400 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/30 dark:hover:border-red-700 dark:hover:bg-red-950/50"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-red-700 dark:text-red-400">{t('picker.issue.defective')}</span>
                    <kbd className="rounded border border-red-300 bg-red-100 px-1.5 py-0.5 text-xs font-mono text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">1</kbd>
                  </div>
                  <p className="mt-1 text-sm text-red-600/80 dark:text-red-400/70">
                    {t('picker.issue.defectiveDesc')}
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => selectIssueType('unavailable')}
                className="flex items-start gap-4 rounded-lg border-2 border-amber-200 bg-amber-50 p-4 text-left transition-colors hover:border-amber-400 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/30 dark:hover:border-amber-700 dark:hover:bg-amber-950/50"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400">
                  <PackageX className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-amber-700 dark:text-amber-400">{t('picker.issue.unavailable')}</span>
                    <kbd className="rounded border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-xs font-mono text-amber-600 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400">2</kbd>
                  </div>
                  <p className="mt-1 text-sm text-amber-600/80 dark:text-amber-400/70">
                    {t('picker.issue.unavailableDesc')}
                  </p>
                </div>
              </button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 ${
                issueType === 'defective' ? 'text-red-600' : 'text-amber-600'
              }`}>
                {issueType === 'defective' ? (
                  <AlertTriangle className="h-5 w-5" />
                ) : (
                  <PackageX className="h-5 w-5" />
                )}
                {issueType === 'defective' ? t('picker.issue.reportDefective') : t('picker.issue.reportMissing')}
              </DialogTitle>
              <DialogDescription>
                {issueType === 'defective'
                  ? t('picker.issue.defectiveBatch', { batchNumber })
                  : t('picker.issue.missingBatch', { batchNumber })
                }
              </DialogDescription>
            </DialogHeader>

            {issueType === 'unavailable' && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <span className="font-medium">{t('picker.issue.whatHappensNext')}</span>
                  <ul className="mt-2 space-y-1 text-sm list-disc list-inside">
                    <li>{t('picker.issue.orderPulled')}</li>
                    <li>{t('picker.issue.garmentsMarked')}</li>
                    <li>{t('picker.issue.coordinatorReview')}</li>
                    <li>{t('picker.issue.autoRebatch')}</li>
                  </ul>
                </div>
              </div>
            )}

            <div className="space-y-4 py-4">
              {/* Back button */}
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors -ml-1"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {t('common:back')}
              </button>

              {/* SKU Selection — searchable combobox */}
              <div className="space-y-2">
                <Label>
                  {issueType === 'defective' ? t('picker.issue.whichDefective') : t('picker.issue.whichMissing')}
                </Label>
                <Popover open={skuPopoverOpen} onOpenChange={setSkuPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={skuPopoverOpen}
                      className="w-full h-12 justify-between font-normal"
                    >
                      {selectedOption ? (
                        <span className="flex items-center gap-2 truncate">
                          <span className="font-mono">{selectedOption.pickSku}</span>
                          <span className="text-muted-foreground truncate">
                            {pileStockLabel(selectedOption)} · {selectedOption.productName}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{t('picker.issue.searchSku')}</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContentInline className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder={t('picker.issue.searchPlaceholder')} />
                      <CommandList>
                        <CommandEmpty>{t('picker.issue.noMatching')}</CommandEmpty>
                        <CommandGroup>
                          {skuOptions.map((opt) => (
                            <CommandItem
                              key={opt.key}
                              value={`${opt.key} ${opt.pickSku} ${opt.sku} ${opt.productName}`}
                              disabled={opt.availableCount === 0}
                              onSelect={() => {
                                setSelectedSku(opt.key);
                                setQuantity(1);
                                setSkuPopoverOpen(false);
                              }}
                              className="min-h-12 py-2.5"
                              data-testid="picking-issue-pile-option"
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4 shrink-0',
                                  selectedSku === opt.key ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                              <div className="flex flex-col min-w-0">
                                <span className="flex items-center gap-2">
                                  <span className="font-mono">{opt.pickSku}</span>
                                  <span className="text-xs font-medium">{pileStockLabel(opt)}</span>
                                </span>
                                <span className="text-xs text-muted-foreground truncate">
                                  {opt.productName} ({t('picker.issue.pileInBatch', { count: opt.availableCount })})
                                </span>
                                {opt.pickSku !== opt.sku && (
                                  <span className="text-xs text-muted-foreground truncate">
                                    {t('picker.issue.pileForOrderSku', { sku: opt.sku })}
                                  </span>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContentInline>
                </Popover>
              </div>

              {/* Quantity */}
              <div className="space-y-2">
                <Label htmlFor="quantity">
                  {issueType === 'defective' ? t('picker.issue.howManyDefective') : t('picker.issue.howManyMissing')}
                </Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 w-12 shrink-0 p-0"
                    onClick={() => adjustQuantity(-1)}
                    disabled={!selectedSku || quantity <= 1}
                  >
                    <Minus className="h-5 w-5" />
                  </Button>
                  <Input
                    id="quantity"
                    type="number"
                    min={1}
                    max={maxQuantity}
                    value={quantity}
                    onChange={handleQuantityChange}
                    className="h-12 text-center"
                    disabled={!selectedSku}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 w-12 shrink-0 p-0"
                    onClick={() => adjustQuantity(1)}
                    disabled={!selectedSku || quantity >= maxQuantity}
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>
                {selectedSku && (
                  <p className="text-xs text-muted-foreground">
                    {t('picker.issue.maxItems', { count: maxQuantity })}
                  </p>
                )}
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">{t('picker.issue.notes')}</Label>
                <Textarea
                  id="notes"
                  placeholder={
                    issueType === 'defective'
                      ? t('picker.issue.notesDefectivePlaceholder')
                      : t('picker.issue.notesMissingPlaceholder')
                  }
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[80px] text-base"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="h-12"
                disabled={isSubmitting}
              >
                {t('common:cancel')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!selectedSku || quantity < 1 || isSubmitting}
                className={`h-12 text-white ${
                  issueType === 'defective'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-amber-600 hover:bg-amber-700'
                }`}
              >
                {isSubmitting
                  ? t('reporting')
                  : issueType === 'defective'
                    ? t('picker.issue.reportDefectiveBtn')
                    : t('picker.issue.reportMissingBtn')
                }
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PickingIssueDialog;
